import { createAxiosInstance } from '../utils/axiosConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8083/api';

// Create axios instance with default config and interceptors
const reviewAPI = createAxiosInstance({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
reviewAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.message || error.response?.data?.error || error.message || 'An error occurred';
    console.error('Review API Error:', errorMessage);
    return Promise.reject(errorMessage);
  }
);

export const reviewService = {
  // Get public product reviews (no auth required)
  getProductReviewsPublic: async (productId, params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const response = await reviewAPI.get(`/reviews/product/${productId}/public`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get public seller reviews (no auth required)
  getSellerReviewsPublic: async (sellerId, params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const response = await reviewAPI.get(`/reviews/seller/${sellerId}/public`, {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get all reviews (authenticated - admin/seller)
  getAllReviews: async (params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc', status } = params;
      const queryParams = { page, size, sortBy, sortDir };
      if (status) queryParams.status = status;
      
      const response = await reviewAPI.get('/reviews', { params: queryParams });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create a new review (authenticated)
  createReview: async (reviewData) => {
    try {
      const response = await reviewAPI.post('/reviews', reviewData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Update a review (authenticated - owner only)
  updateReview: async (reviewId, reviewData) => {
    try {
      const response = await reviewAPI.put(`/reviews/${reviewId}`, reviewData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Delete a review (authenticated - owner/admin)
  deleteReview: async (reviewId) => {
    try {
      const response = await reviewAPI.delete(`/reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get review by ID (authenticated)
  getReviewById: async (reviewId) => {
    try {
      const response = await reviewAPI.get(`/reviews/${reviewId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Approve a review (admin/seller only)
  approveReview: async (reviewId) => {
    try {
      const response = await reviewAPI.patch(`/reviews/${reviewId}/approve`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reject a review (admin/seller only)
  rejectReview: async (reviewId) => {
    try {
      const response = await reviewAPI.patch(`/reviews/${reviewId}/reject`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get user's reviews (authenticated)
  getUserReviews: async (params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const response = await reviewAPI.get('/reviews/my-reviews', {
        params: { page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get reviews for seller's products (authenticated seller)
  getSellerProductReviews: async (params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc', status } = params;
      const queryParams = { page, size, sortBy, sortDir };
      if (status) queryParams.status = status;
      
      const response = await reviewAPI.get('/reviews/seller-products', { params: queryParams });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get review analytics for a product (public)
  // NOTE: Analytics are already included in getProductReviewsPublic response
  // This method is kept for backward compatibility but should be avoided
  // Use analytics from the reviews response instead to avoid duplicate API calls
  getProductReviewAnalytics: async (productId) => {
    try {
      // Use a minimal page size to reduce data transfer when only analytics are needed
      const response = await reviewAPI.get(`/reviews/product/${productId}/public`, {
        params: { page: 0, size: 1 } // Minimal data since we only need analytics
      });
      return response.data.analytics;
    } catch (error) {
      throw error;
    }
  },

  // Get review analytics for a seller (public)
  getSellerReviewAnalytics: async (sellerId) => {
    try {
      const response = await reviewAPI.get(`/reviews/seller/${sellerId}/public`, {
        params: { page: 0, size: 1 } // We only need analytics, so minimal data
      });
      return response.data.analytics;
    } catch (error) {
      throw error;
    }
  },

  // Report a review (authenticated)
  reportReview: async (reviewId, reason) => {
    try {
      const response = await reviewAPI.post(`/reviews/${reviewId}/report`, { reason });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Mark review as helpful (authenticated)
  markReviewHelpful: async (reviewId, helpful = true) => {
    try {
      // Get user ID from localStorage or token
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const userId = user.id;
      
      if (!userId) {
        throw new Error('User ID not found. Please login again.');
      }
      
      const endpoint = helpful ? 'helpful' : 'not-helpful';
      const response = await reviewAPI.post(`/reviews/${reviewId}/${endpoint}?userId=${userId}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get recent reviews (public)
  getRecentReviews: async (params = {}) => {
    try {
      const { days = 30, limit = 20, page, size } = params;
      // Support both old (page/size) and new (days/limit) parameter formats
      const queryParams = {};
      if (days !== undefined) queryParams.days = days;
      if (limit !== undefined) queryParams.limit = limit;
      if (page !== undefined) queryParams.page = page;
      if (size !== undefined) queryParams.size = size;
      
      const response = await reviewAPI.get('/reviews/recent', {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Search reviews (authenticated)
  searchReviews: async (searchTerm, params = {}) => {
    try {
      const { page = 0, size = 10, sortBy = 'createdAt', sortDir = 'desc' } = params;
      const response = await reviewAPI.get('/reviews/search', {
        params: { q: searchTerm, page, size, sortBy, sortDir }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Upload review images (if supported)
  uploadReviewImages: async (files) => {
    try {
      const formData = new FormData();
      files.forEach((file, index) => {
        formData.append(`images`, file);
      });

      const response = await reviewAPI.post('/reviews/upload-images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Add seller response to review (authenticated - seller only)
  addSellerResponse: async (reviewId, response) => {
    try {
      const apiResponse = await reviewAPI.post(`/reviews/${reviewId}/seller-response`, {
        response: response
      });
      return apiResponse.data;
    } catch (error) {
      throw error;
    }
  },

  // Flag review for inappropriate content (authenticated)
  flagReview: async (reviewId, reason) => {
    try {
      const response = await reviewAPI.post(`/reviews/${reviewId}/flag`, {
        reason: reason
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get top reviews (public - no auth required)
  getTopReviews: async (params = {}) => {
    try {
      const { type = 'rated', limit = 10 } = params;
      const response = await reviewAPI.get('/reviews/top', {
        params: { type, limit }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
};

export default reviewService;